import { useSnackbar } from 'notistack';
import {
  ChangeEvent,
  Dispatch,
  HtmlHTMLAttributes,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  getSiteProcessDetail,
  getSiteProcessList,
  updateSiteProcess
} from '../../../../apicalls/process';
import { TSiteProcessListData } from '../../../../types/process';
import { checkResData } from '../../../../utils/common';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';
import { log } from '../../../../utils';

export type TProps = {
  constructId: number;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  renderSiteProcessDataTrigger?: boolean;
  reRenderSiteProcessData: () => void;
};

interface ResourceItem {
  id: number;
  order_id: number;
  parent_site_process_id?: number;
}

export const useHooks = (props: TProps) => {
  const {
    constructId,
    currentPage,
    setCurrentPage,
    renderSiteProcessDataTrigger,
    reRenderSiteProcessData
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  // ページネーション用 現在のページ番号のstateはここで管理しない。親要素から受け取ったものを利用する。
  const [displayLimit, setDisplayLimit] = useState<number>(10); // 表示件数(10,30,50)
  const [pageCount, setPageCount] = useState(1); // 総ページ数

  // ローディング表示制御
  const currentConstructId = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);

  // 編集モーダル用
  const [editSiteProcessId, setEditSiteProcessId] = useState(0);
  const [isOpenSiteProcessEditModal, setIsOpenSiteProcessEditModal] =
    useState(false);

  // 削除モーダル用
  const [deleteSiteProcessId, setDeleteSiteProcessId] = useState(0);
  const [isOpenSiteProcessDeleteModal, setIsOpenSiteProcessDeleteModal] =
    useState(false);

  // リストのデータを入手
  const [siteProcessList, setSiteProcessList] = useState<
    Array<TSiteProcessListData>
  >([]);

  // 実働人工の差分
  const createManDayText = (
    actualManDay: number | null | undefined,
    plannedManDay: number | null | undefined
  ) => {
    // actual_man_day なし
    if (actualManDay === undefined || actualManDay === null) {
      return '--';
    } // actual_man_day あり planned_man_day なし
    if (plannedManDay === undefined || plannedManDay === null) {
      return actualManDay + '人日(-)';
    } // actual_man_day,planned_man_day あり
    if (actualManDay > plannedManDay) {
      return (
        actualManDay + '人日' + '(+' + (actualManDay - plannedManDay) + ')'
      );
    }
    if (actualManDay < plannedManDay) {
      return actualManDay + '人日' + '(' + (actualManDay - plannedManDay) + ')';
    }
    return actualManDay + '人日(0)';
  };

  // 工程のステータスを更新する
  const updateStatus = (siteProcessId: number, status: number) => {
    getSiteProcessDetail(siteProcessId).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          updateSiteProcess(
            data.data.id,
            data.data.site_process_name,
            data.data.constructs.id,
            data.data.start_date,
            status,
            data.data.parent_site_process_id,
            data.data.charge_user?.id,
            data.data.end_date,
            parseNumber(data.data.planned_man_day),
            parseNumber(data.data.actual_man_day),
            data.data.note
          ).then((resData: any) => {
            checkResData(resData, enqueueSnackbar).then(() => {
              reRenderSiteProcessData();
            });
          });
        }
      });
    });
  };

  // ステータス変更時の処理
  const handleChangeStatus = (
    e: ChangeEvent<HTMLSelectElement>,
    siteProcessId: number
  ) => {
    const status = parseNumber(e.target.value) ?? 0;
    updateStatus(siteProcessId, status);
  };

  // 表示件数変更時
  const handleChangeDisplayLimit = (e: ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(1);
    setDisplayLimit(parseInt(e.target.value));
  };

  useEffect(() => {
    if (isLoading || isLoadingCurrent) return;

    if (constructId > 0) {
      if (currentConstructId.current !== constructId) {
        currentConstructId.current = constructId;
        setIsLoading(true);
      } else {
        setIsLoadingCurrent(true);
      }

      getSiteProcessList(
        constructId,
        undefined,
        undefined,
        undefined,
        currentPage,
        displayLimit
      ).then(resData => {
        checkResData(resData, enqueueSnackbar).then(data => {
          if (data) {
            // 工程削除でページが減るときの処理
            if (currentPage > data.page_count) {
              setCurrentPage(data.page_count);
            }

            // parent_site_process_idを持つ要素を適切な位置に移動
            const adjustedData = data.data.map(
              (item: any, index: any, array: any) => {
                if (item.parent_site_process_id !== undefined) {
                  // 親を探す
                  const parentIndex = array.findIndex(
                    (parent: ResourceItem) =>
                      parent.id === item.parent_site_process_id
                  );
                  if (parentIndex !== -1) {
                    // 親のorder_idを取得
                    const parentOrderId = array[parentIndex].order_id;
                    // 子供のリストを取得
                    const children = array.filter(
                      (child: ResourceItem) =>
                        child.parent_site_process_id ===
                        item.parent_site_process_id
                    );
                    // 子供をorder_idで昇順に並べ替え
                    children.sort(
                      (a: ResourceItem, b: ResourceItem) =>
                        a.order_id - b.order_id
                    );
                    // 子供の順番に基づいてorder_idを設定
                    children.forEach((child: ResourceItem, i: number) => {
                      const newOrderId = parentOrderId + 1 + i;
                      // 既に設定されているorder_idが新しいorder_id以上の要素を1つずつずらす
                      for (let j = 0; j < array.length; j++) {
                        if (array[j].order_id >= newOrderId) {
                          array[j].order_id += 1;
                        }
                      }
                      // 子供のorder_idを新しい値に設定
                      child.order_id = newOrderId;
                    });
                  }
                }
                return item;
              }
            );

            // order_idでソート
            const sortedData = adjustedData.sort(
              (a: ResourceItem, b: ResourceItem) => a.order_id - b.order_id
            );

            setSiteProcessList(sortedData);
            setPageCount(data.page_count);
            window.scrollTo(0, 0);
          }
          setIsLoading(false);
          setIsLoadingCurrent(false);
        });
      });
    }
  }, [constructId, currentPage, displayLimit, renderSiteProcessDataTrigger]);

  // 実績記録用：コンポーネント描画時にログ出力
  useEffect(() => {
    log.info('to process view mode: list');
  }, []);

  return {
    displayLimit,
    handleChangeDisplayLimit,
    siteProcessList,
    setSiteProcessList,
    pageCount,
    setPageCount,
    editSiteProcessId,
    setEditSiteProcessId,
    isOpenSiteProcessEditModal,
    setIsOpenSiteProcessEditModal,
    deleteSiteProcessId,
    setDeleteSiteProcessId,
    isOpenSiteProcessDeleteModal,
    setIsOpenSiteProcessDeleteModal,
    createManDayText,
    handleChangeStatus,
    isLoading,
    isLoadingCurrent
  };
};
